<script lang="ts" setup>
  import { useApi } from '@/composables/use-api.ts';
  import { router } from '@/routes/router.ts';
  import { ApiFlexError } from '@/services/api/api-error.ts';
  import { ApprovalApplicationRequest } from '@/services/api/approval-api.ts';
  import {
    calExtraWorkDayTime,
    calExtraWorkNightTime,
    calHolidayWorkDayTime,
    calHolidayWorkNightTime,
    calNightWorkTime,
    convertHoursAndMinutesToSeconds,
    secondsToHoursAndMinutes,
  } from '@/services/approval-application-svc.ts';
  import {
    loadAndErrorHandleAsync,
    loadWithStatusAndErrorHandleAsync,
  } from '@/services/loading-svc.ts';
  import { showBackEndFlexErrorMessage } from '@/utils/error-message-helper.ts';
  import dayjs from 'dayjs';
  import { computed, onMounted, reactive, ref, watch } from 'vue';

  const { approval, attendance } = useApi();
  const loading = ref<boolean>(false);
  const isDayWorkInput = computed(() => {
    return formInputs.type == 'extraWork' || formInputs.type == 'holidayWork';
  });
	const commentLength = ref<number>(0);

  const isNightWorkInput = computed(() => {
    return (
      formInputs.type == 'nightWork' ||
      formInputs.type == 'holidayWork' ||
      formInputs.type == 'extraWork'
    );
  });

  const guideText = computed(() => {
    const type = formInputs.type;
    const applicationType = applicationTypes.find((x) => x.value == type);
    if (applicationType) {
      return applicationType.desc;
    }
    return '';
  });

  const formInputs = reactive({
    type: '',
    date: dayjs(new Date()).format('YYYY-MM-DD'),
    dayHours: 0,
    dayMinutes: 0,
    nightHours: 0,
    nightMinutes: 0,
    comment: '',
  });

  async function submitAsync() {
    console.log(formInputs);
    // 다른 로직
    if (!validate()) return;
    try {
      await loadWithStatusAndErrorHandleAsync(
        (status) => {
          loading.value = status;
        },
        async () => {
          const applicationRequest: ApprovalApplicationRequest = {
            date: dayjs(formInputs.date).format('YYYY-MM-DD'),
            dayTime: convertHoursAndMinutesToSeconds(
              formInputs.dayHours,
              formInputs.dayMinutes
            ),
            nightTime: convertHoursAndMinutesToSeconds(
              formInputs.nightHours,
              formInputs.nightMinutes
            ),
            comment: formInputs.comment,
          };
          await approval.createAsync(formInputs.type, applicationRequest);

          const applicationType = applicationTypes.find(
            (x) => x.value == formInputs.type
          );
          if (applicationType) {
            alert(`${applicationType.text}가 신청 되었습니다.`);
          }
          router.back();
        }
      );
    } catch (error) {
      if (error instanceof ApiFlexError && error.value.resultCode == 400) {
        showBackEndFlexErrorMessage(
          '결재 신청 중 오류가 발생하였습니다.',
          error
        );
        return;
      }
      throw error;
    }
  }

  function validate() {
    if (!formInputs.type) {
      alert('신청 유형을 입력해주세요.');
      return false;
    }
    if (!formInputs.date) {
      alert('신청 근무일을 입력해주세요.');
      return false;
    }
    if (
      !formInputs.nightHours &&
      !formInputs.nightMinutes &&
      !formInputs.dayHours &&
      !formInputs.dayMinutes
    ) {
      if (isNightWorkInput.value && !isDayWorkInput.value) {
        alert('야간 신청 시간을 입력해주세요.');
      } else alert('주간 또는 야간 신청 시간을 입력해주세요.');

      return false;
    }
    if (!formInputs.comment) {
      alert('사유를 입력해주세요.');
      return false;
    }
    return true;
  }

  function cancel() {
    router.push({ name: 'Approval' });
  }

  function onTypeChange() {
    if (guideText.value) {
      alert(guideText.value);
    }
    resetValue();
  }

  function resetValue() {
    formInputs.dayHours = 0;
    formInputs.dayMinutes = 0;
    formInputs.nightHours = 0;
    formInputs.nightMinutes = 0;
    formInputs.comment = '';
  }

  const applicationTypes = [
    {
      text: '연장근로',
      value: 'extraWork',
      desc: '의무 근로 시간을 초과한 경우 신청이 가능합니다.',
    },
    {
      text: '야간근로',
      value: 'nightWork',
      desc: '22:00 ~ 익일 06:00 에 근무한 경우 신청이 가능합니다.',
    },
    {
      text: '휴일근로',
      value: 'holidayWork',
      desc: '법정공휴일 또는 회사에서 정한 휴일에 근무한 경우 신청이 가능합니다.\n\n법정공휴일이 아닌 토요일은 신청 할수 없습니다.',
    },
  ];

  async function loadFormInputFromQuery() {
    const { appType, id } = router.currentRoute.value.query;

    const type = appType as string;
    const attendanceId = Number(id);

    console.log('loadFormInputFromQuery: ', type, attendanceId);

    if (!type || !attendanceId) return;

    formInputs.type = type;
    await loadAndErrorHandleAsync(async () => {
      const data = await attendance.day.getWithApprovalsByIdAsync(attendanceId);
      if (!data) return;

      formInputs.date = dayjs(data.date).format('YYYY-MM-DD');
      if (type == 'nightWork') {
        const { hours, minutes } = secondsToHoursAndMinutes(
          calNightWorkTime(data)
        );
        formInputs.nightHours = hours;
        formInputs.nightMinutes = minutes;
        return;
      }
      if (type == 'extraWork') {
        const { hours, minutes } = secondsToHoursAndMinutes(
          calExtraWorkDayTime(data)
        );
        formInputs.dayHours = hours;
        formInputs.dayMinutes = minutes;

        const { hours: nightHours, minutes: nightMinutes } =
          secondsToHoursAndMinutes(calExtraWorkNightTime(data));
        formInputs.nightHours = nightHours;
        formInputs.nightMinutes = nightMinutes;
        return;
      }

      if (type == 'holidayWork') {
        const { hours, minutes } = secondsToHoursAndMinutes(
          calHolidayWorkDayTime(data)
        );
        formInputs.dayHours = hours;
        formInputs.dayMinutes = minutes;

        const { hours: nightHours, minutes: nightMinutes } =
          secondsToHoursAndMinutes(calHolidayWorkNightTime(data));
        formInputs.nightHours = nightHours;
        formInputs.nightMinutes = nightMinutes;
      }
    });
  }

  watch(
    () => formInputs.dayHours,
    (newValue) => {
      formInputs.dayHours = newValue > 23 ? 23 : newValue > 0 ? newValue : 0;
    }
  );
  watch(
    () => formInputs.dayMinutes,
    (newValue) => {
      formInputs.dayMinutes = newValue > 59 ? 59 : newValue > 0 ? newValue : 0;
    }
  );
  watch(
    () => formInputs.nightHours,
    (newValue) => {
      formInputs.nightHours = newValue > 23 ? 23 : newValue > 0 ? newValue : 0;
    }
  );
  watch(
    () => formInputs.nightMinutes,
    (newValue) => {
      formInputs.nightMinutes =
        newValue > 59 ? 59 : newValue > 0 ? newValue : 0;
    }
  );
  onMounted(async () => {
    console.log(
      'ApprovalRequestPage router query: ',
      router.currentRoute.value.query
    );

    await loadFormInputFromQuery();
  });
</script>

<template>
  <DefaultLayout
    go-back
    title="근태 신청">
    <div class="px-4 card card-compact">
      <div class="card-body">
        <FormField title="신청 유형">
          <select
            v-model="formInputs.type"
            class="w-full select select-bordered"
            @change="onTypeChange">
            <option
              disabled
              selected>
              신청 유형
            </option>
            <option
              v-for="type in applicationTypes"
              :key="type.value"
              :value="type.value">
              {{ type.text }}
            </option>
          </select>
        </FormField>

        <FormField title="신청 근무일">
          <input
            v-model="formInputs.date"
            class="w-full input input-bordered"
            type="date" />
        </FormField>

        <div v-if="isDayWorkInput">
          <FormField title="주간 신청 시간">
            <div class="flex justify-center items-center">
              <input
                v-model.number="formInputs.dayHours"
                class="flex-1 text-center input input-bordered mr-2"
                max="23"
                min="0"
                type="number" />
              시간

              <input
                v-model.number="formInputs.dayMinutes"
                class="flex-1 text-center input input-bordered mx-2"
                max="59"
                min="0"
                type="number" />
              분
            </div>
          </FormField>
        </div>

        <div v-if="isNightWorkInput">
          <FormField title="야간 신청 시간">
            <div class="flex justify-center items-center">
              <input
                v-model.number="formInputs.nightHours"
                class="flex-1 text-center input input-bordered mr-2"
                max="23"
                min="0"
                type="number" />
              시간
              <input
                v-model.number="formInputs.nightMinutes"
                class="flex-1 text-center input input-bordered mx-2"
                max="59"
                min="0"
                type="number" />
              분
            </div>
          </FormField>
        </div>

        <FormField title="사유">
          <textarea
            v-model="formInputs.comment"
            class="w-full textarea textarea-bordered flex-col"
            maxlength="255"
            placeholder="사유를 입력합니다."
            @input="(e) => { const target = e.target as HTMLTextAreaElement; commentLength = target.value.length; }"
          />
	        <span class="text-sm float-right">{{commentLength}}/255</span>
        </FormField>
      </div>
    </div>
    <div class="flex items-center justify-center mt-5 join">
      <button
        :disabled="loading"
        class="w-32 join-item btn"
        @click="cancel">
        취소
      </button>
      <button
        :disabled="loading"
        class="w-32 join-item btn btn-primary"
        @click="submitAsync">
        <span
          v-if="loading"
          class="loading loading-spinner" />
        신청
      </button>
    </div>
  </DefaultLayout>
</template>
